import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useCountdown, fmtClock, END_REASONS } from '../../lib/firmAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'İşlem başarısız');

const ActiveStrip = ({ session, onRevoke }) => {
  const left = useCountdown(session.expires_at);
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="firm-access-active-strip">
      <ShieldAlert className="h-5 w-5 shrink-0 text-red-600" />
      <span className="flex-1 min-w-[200px]">
        İSG uzmanı <b>{session.staff_name}</b> şu anda firmanız adına erişiyor. Kalan süre: <b className="font-mono tabular-nums" data-testid="firm-access-active-countdown">{fmtClock(left)}</b>
      </span>
      <Button size="sm" variant="destructive" onClick={() => onRevoke(session)} data-testid="firm-access-revoke-btn">Erişimi İptal Et</Button>
    </div>
  );
};

// Firma ekranı: erişim taleplerini onay popup'ı, aktif erişim şeridi ve "erişim sona erdi" bildirimi
export const FirmAccessWatcher = () => {
  const [state, setState] = useState({ pending: [], active: [], ended: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/firm-access/firm/status`).then(r => setState(r.data)).catch(() => {});
  }, []);

  // Değişiklikler anlık bildirimle gelir ('firm-access'); 60 sn yedek kontrol + sekmeye dönüş
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener('firm-access', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); window.removeEventListener('firm-access', load); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);
  // Talep (10 dk) veya erişim süresi dolduğu anda ekran tazelenir (sunucu süre dolunca yetkiyi zaten kapatır)
  useEffect(() => {
    const ends = [...(state?.pending || []).map(s => new Date(s.requested_at).getTime() + 10 * 60000), ...(state?.active || []).map(s => new Date(s.expires_at).getTime())];
    if (!ends.length) return undefined;
    const t = setTimeout(load, Math.max(0, Math.min(...ends) - Date.now()) + 1000);
    return () => clearTimeout(t);
  }, [state, load]);

  const act = async (s, action, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/firm-access/firm/${s.id}/${action}`);
      if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    load();
  };

  const pending = state.pending[0];
  const ended = !pending && state.ended[0];

  return (
    <>
      {state.active.map(s => (
        <ActiveStrip key={s.id} session={s} onRevoke={(x) => act(x, 'revoke', 'Firma erişimi iptal edildi')} />
      ))}
      <AlertDialog open={!!pending}>
        <AlertDialogContent className="z-[100]" data-testid="firm-access-request-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-600" /> Firma Erişim Talebi</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="text-base text-slate-900" data-testid="firm-access-request-text">
                  İSG uzmanı <b>{pending?.staff_name}</b>, <b>{pending?.minutes} dakikalık</b> erişim istiyor, onaylıyor musunuz?
                </p>
                <p>Onaylarsanız uzman bu süre boyunca firma portalınızı firmanız adına kullanabilir ve işlem yapabilir. Erişimi istediğiniz an iptal edebilirsiniz; tüm işlemler kayıt altına alınır.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => act(pending, 'reject', 'Erişim talebi reddedildi')} data-testid="firm-access-reject-btn">Reddet</Button>
            <Button disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(pending, 'approve', 'Erişim onaylandı')} data-testid="firm-access-approve-btn">
              <ShieldCheck className="h-4 w-4 mr-1" /> Onayla
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!ended}>
        <AlertDialogContent className="z-[100]" data-testid="firm-access-ended-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldOff className="h-5 w-5 text-slate-600" /> Erişim Sona Erdi</AlertDialogTitle>
            <AlertDialogDescription data-testid="firm-access-ended-text">
              İSG uzmanı <b>{ended?.staff_name}</b> için açılan firma erişimi sona erdi ({END_REASONS[ended?.end_reason] || 'Süre doldu'}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => act(ended, 'ack')} data-testid="firm-access-ended-ok-btn">Tamam</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
